import type { Grid, BlokkerStatus } from './game'

export const BLOKKER = {
  PROB_CAP: 0.75,
  PROB_CAP_AT_POINTS: 5000,
  STAGE_3_DEMAND: 1000,
  START_TURNS: 4,
} as const

export function spawnProbability(totalPoints: number): number {
  if (totalPoints <= 0) return 0
  return Math.min(BLOKKER.PROB_CAP, BLOKKER.PROB_CAP * (totalPoints / BLOKKER.PROB_CAP_AT_POINTS))
}

function statusForTurns(turns: number): BlokkerStatus {
  if (turns <= 0) return { stage: 3, remaining: BLOKKER.STAGE_3_DEMAND }
  if (turns <= 3) return { stage: 2, turns }
  return { stage: 1, turns }
}

export function borderColorForTurns(turns: number): 'yellow' | 'orange' | 'red' {
  if (turns >= 4) return 'yellow'
  if (turns >= 2) return 'orange'
  return 'red'
}

export interface Position { row: number; col: number }

export interface BlokkerTickInputs {
  totalPoints: number
  tickTurns: boolean
  rollSpawn: boolean
}

export interface BlokkerTickResult {
  grid: Grid
  depleted: Position[]
  spawned: Position | null
  promoted: Position[]
  spawnRolled: boolean
}

function pickCandidate(grid: Grid, rng: () => number): Position | null {
  const candidates: Position[] = []
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      const card = grid[r][c]
      if (card && !card.blokker) candidates.push({ row: r, col: c })
    }
  }
  if (candidates.length === 0) return null
  return candidates[Math.floor(rng() * candidates.length)]
}

export function tickStage3(grid: Grid, scoredPoints: number): Grid {
  if (scoredPoints <= 0) return grid
  let mutated = false
  const newGrid: Grid = grid.map(row => row.slice())
  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      const card = newGrid[r][c]
      if (card?.blokker?.stage !== 3) continue
      const next = Math.max(0, card.blokker.remaining - scoredPoints)
      if (next === card.blokker.remaining) continue
      newGrid[r][c] = { ...card, blokker: { stage: 3, remaining: next } }
      mutated = true
    }
  }
  return mutated ? newGrid : grid
}

export function applyBlokkerTick(
  grid: Grid,
  inputs: BlokkerTickInputs,
  rng: () => number,
): BlokkerTickResult {
  const { totalPoints, tickTurns, rollSpawn } = inputs
  const newGrid: Grid = grid.map(row => row.slice())
  const depleted: Position[] = []
  const promoted: Position[] = []
  let spawned: Position | null = null
  let spawnRolled = false

  for (let r = 0; r < 5; r++) {
    for (let c = 0; c < 5; c++) {
      const card = newGrid[r][c]
      if (card?.blokker?.stage === 3 && card.blokker.remaining === 0) {
        depleted.push({ row: r, col: c })
      }
    }
  }

  if (tickTurns) {
    for (let r = 0; r < 5; r++) {
      for (let c = 0; c < 5; c++) {
        const card = newGrid[r][c]
        if (!card?.blokker || card.blokker.stage === 3) continue
        const before = card.blokker
        const after = statusForTurns(before.turns - 1)
        newGrid[r][c] = { ...card, blokker: after }
        if (before.stage !== after.stage) promoted.push({ row: r, col: c })
      }
    }
  }

  if (rollSpawn) {
    spawnRolled = true
    if (rng() < spawnProbability(totalPoints)) {
      const target = pickCandidate(newGrid, rng)
      if (target) {
        const card = newGrid[target.row][target.col]!
        newGrid[target.row][target.col] = { ...card, blokker: { stage: 1, turns: BLOKKER.START_TURNS } }
        spawned = target
      }
    }
  }

  return { grid: newGrid, depleted, spawned, promoted, spawnRolled }
}
